import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Star, MapPin, Car, Languages, ArrowLeft, CalendarClock } from "lucide-react";
import { initials, formatDateLong, DAYS } from "@/lib/format";
import { toast } from "sonner";

export default function InstructorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [instructor, setInstructor] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [booking, setBooking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, a, s] = await Promise.all([
        api.get(`/instructors/${id}`),
        api.get(`/instructors/${id}/availability`),
        api.get(`/instructors/${id}/slots`),
      ]);
      setInstructor(p.data); setAvailability(a.data); setSlots(s.data);
    } catch (e) { toast.error("Could not load instructor"); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const book = async () => {
    setBooking(true);
    try {
      await api.post("/bookings", {
        instructor_id: id, date: selected.date, start_time: selected.start_time, end_time: selected.end_time, phone, notes,
      });
      toast.success("A instructor will be in contact with you soon.");
      setSelected(null); setNotes(""); setPhone("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBooking(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background"><Navbar /><div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-arctic-primary" /></div></div>
  );
  if (!instructor) return null;

  // group open slots by date
  const byDate = {};
  slots.forEach((s) => { (byDate[s.date] = byDate[s.date] || []).push(s); });
  const dates = Object.keys(byDate).sort();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Button variant="ghost" onClick={() => navigate("/instructors")} className="mb-4 -ml-2" data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>

        <Card className="p-6 rounded-2xl border-border">
          <div className="flex flex-col sm:flex-row gap-6">
            <Avatar className="w-24 h-24 border border-border">
              <AvatarImage src={instructor.photo} alt={instructor.name} />
              <AvatarFallback className="bg-arctic-secondary text-arctic-accent text-2xl font-heading">{initials(instructor.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h1 className="font-heading font-extrabold text-2xl text-arctic-secondary" data-testid="instructor-name">{instructor.name}</h1>
              <div className="flex items-center gap-1 text-amber-500 mt-1">
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {instructor.rating?.toFixed(1)}
                <span className="text-muted-foreground">· {instructor.experience_years} years experience</span>
              </div>
              <p className="mt-3 text-muted-foreground leading-relaxed">{instructor.bio}</p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-muted-foreground">
                {instructor.location && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {instructor.location}</div>}
                {instructor.vehicle && <div className="flex items-center gap-2"><Car className="w-4 h-4" /> {instructor.vehicle}</div>}
                {instructor.languages && <div className="flex items-center gap-2"><Languages className="w-4 h-4" /> {instructor.languages}</div>}
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading font-extrabold text-2xl text-arctic-secondary">${instructor.hourly_rate}</div>
              <div className="text-xs text-muted-foreground">per lesson</div>
            </div>
          </div>
        </Card>

        {/* Weekly schedule summary */}
        <div className="mt-8">
          <h2 className="font-heading font-bold text-xl text-arctic-secondary flex items-center gap-2"><CalendarClock className="w-5 h-5 text-arctic-primary" /> Weekly availability</h2>
          <div className="flex flex-wrap gap-2 mt-3">
            {availability.length === 0 ? <p className="text-muted-foreground text-sm">No availability set.</p> :
              availability.map((s) => (
                <span key={s.slot_id} className="text-xs bg-muted rounded-full px-3 py-1 text-arctic-secondary">
                  {DAYS[s.day_of_week].slice(0, 3)} {s.start_time}–{s.end_time}
                </span>
              ))}
          </div>
        </div>

        {/* Bookable slots */}
        <div className="mt-8">
          <h2 className="font-heading font-bold text-xl text-arctic-secondary">Book an open slot</h2>
          {dates.length === 0 ? (
            <p className="text-muted-foreground text-sm mt-3">No open slots in the next few weeks.</p>
          ) : (
            <div className="space-y-4 mt-4" data-testid="open-slots">
              {dates.map((d) => (
                <Card key={d} className="p-4 rounded-2xl border-border">
                  <div className="font-heading font-semibold text-arctic-secondary mb-3">{formatDateLong(d)}</div>
                  <div className="flex flex-wrap gap-2">
                    {byDate[d].map((s, i) => (
                      <button key={i} onClick={() => setSelected(s)} data-testid={`slot-${d}-${s.start_time}`}
                        className="rounded-full border border-arctic-primary/40 text-arctic-primary hover:bg-arctic-primary hover:text-white px-4 py-1.5 text-sm font-medium transition-colors duration-200">
                        {s.start_time}–{s.end_time}
                      </button>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request this lesson</DialogTitle>
            <DialogDescription>
              {selected && <>{formatDateLong(selected.date)} at {selected.start_time}–{selected.end_time} with {instructor.name}.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Contact phone number <span className="text-destructive">*</span></label>
            <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 555-0142" data-testid="booking-phone" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Note to instructor (optional)</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. I'm a nervous beginner, mostly need highway practice." data-testid="booking-notes" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelected(null)}>Cancel</Button>
            <Button onClick={book} disabled={booking || !phone.trim()} data-testid="confirm-booking" className="rounded-full bg-arctic-primary hover:bg-arctic-primary/90">
              {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
